import React, { useState, useEffect, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useAppData } from "../../../context/AppDataContext";
import NovelEditor from "../../core/NovelEditor";
import TaskTable from "../../task/TaskTable";
import TaskAssignments from "../../task/TaskAssignments";
import TimeTrackingButton from "../../forms/TimeTrackingButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEllipsisV,
  faListCheck,
  faCalendarDays,
} from "@fortawesome/free-solid-svg-icons";
import FormFieldTags from "../../input/FormFieldTags";
import CommentsActivityLogs from "../../task/CommentsActivityLogs";
import TaskDependencies from "../../task/TaskDependencies";
import useClickOutside from "../../../hooks/useClickOutside";
import { successfulToast, errorToast } from "../../../util/toastNotifications";
import { setCachedBreadcrumbLabel } from "../../../helpers/breadcrumbCache";
import { parseDescriptionForEditor } from "../../../utils/parseDescriptionForEditor";
import ConfirmDelete from "../../modals/ConfirmDelete";
import DragDrop from "../../DragDrop";
import FilePreview from "../../files/FilePreview";
import ProjectDropdown from "../../dropdown/ProjectDropdown";
import StatusDropdown from "../../dropdown/StatusDropdown";
import PriorityDropdown from "../../dropdown/PriorityDropdown";
import PointsDropdown from "../../dropdown/PointsDropdown";
import useProjectChange from "../../../hooks/task/useProjectChange";
import usePriorityChange from "../../../hooks/task/usePriorityChange";
import usePointsChange from "../../../hooks/task/usePointsChange";
import { fetchProjectsForSelect } from "../../../util/fetchProjectsForSelect";
import { getDownloadUrl } from "../../../util/fileUrls";
import { handleFileDownload } from "../../../util/downloadFile";
import Loading from "../../layout/Loading";

const TaskDetailPage = () => {
  const { taskId } = useParams();
  const history = useHistory();
  const { api } = useAppData();
  const [task, setTask] = useState(null);
  const [subtasks, setSubtasks] = useState([]);
  const [taskStatuses, setTaskStatuses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [showPointsDropdown, setShowPointsDropdown] = useState(false);
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const [description, setDescription] = useState(null);
  const [isSavingDescription, setIsSavingDescription] = useState(false);
  const originalDescriptionRef = useRef(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [previewFile, setPreviewFile] = useState(null);

  const dropdownRef = useClickOutside(() => setIsDropdownOpen(false));

  useEffect(() => {
    const fetchTaskData = async () => {
      try {
        setLoading(true);
        const [taskData, statusesData] = await Promise.all([
          api.task?.getTaskById(taskId),
          api.taskStatus.getTaskStatuses(),
        ]);

        setTask(taskData);
        setSubtasks(taskData.subtasks || []);
        setTaskStatuses(statusesData);

        const taskProjectId =
          taskData?.project_id ??
          taskData?.project?.project_id ??
          taskData?.project?.id ??
          null;

        const projectsData = await fetchProjectsForSelect(api, {
          currentProjectId: taskProjectId,
        });
        setProjects(projectsData);
        setShowStatusDropdown(false);
        setShowProjectDropdown(false);
        setShowPointsDropdown(false);
        setShowPriorityDropdown(false);

        const normalized = (taskData.files ?? []).map((f) => ({
          ...f,
          preview_url: f.preview_url ?? f.file_url ?? null,
        }));
        setUploadedFiles(normalized);

        const { doc, storedString } = parseDescriptionForEditor(
          taskData.description
        );
        setDescription(doc);
        originalDescriptionRef.current =
          storedString ?? (doc ? JSON.stringify(doc) : null);
      } catch (error) {
        console.error("Error fetching task data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTaskData();
  }, [api, taskId]);

  useEffect(() => {
    if (task?.task_id && task?.name) {
      setCachedBreadcrumbLabel("task", task.task_id, task.name);
    }
  }, [task]);

  const saveDescription = async () => {
    if (description === null && !originalDescriptionRef.current) {
      return;
    }

    const descriptionString = JSON.stringify(description);
    if (descriptionString === originalDescriptionRef.current) {
      return;
    }

    try {
      setIsSavingDescription(true);
      await api.task.updateTask(taskId, {
        description: descriptionString,
        version: task.version,
      });
      const updatedTask = await api.task.getTaskById(taskId);
      setTask(updatedTask);
      originalDescriptionRef.current = descriptionString;
    } catch (error) {
      if (error && error.status === 409) {
        errorToast(
          "This task has changed since you started editing. Copy your changes, refresh the page, and try again."
        );
      } else {
        errorToast("Failed to update task description");
      }
    } finally {
      setIsSavingDescription(false);
    }
  };

  const hasAssignee = task?.assignments.length > 0 === true;

  const handleTagRemove = async (tagId, tag) => {
    try {
      const tagName = (tag?.name || "").toLowerCase();
      if (tagName === "blocked" || tagName === "blocking") {
        const dependencyTaskIds = (task?.dependencies || [])
          .map((dep) => dep?.task_id)
          .filter(Boolean);
        const dependantTaskIds = (task?.dependants || [])
          .map((dep) => dep?.task_id)
          .filter(Boolean);

        await api.task.updateTaskDependencies(taskId, {
          dependency_task_ids: tagName === "blocked" ? [] : dependencyTaskIds,
          dependant_task_ids: tagName === "blocking" ? [] : dependantTaskIds,
        });
        const refreshedTask = await api.task.getTaskById(taskId);
        setTask(refreshedTask);
        return;
      }

      const updated = await api.task.toggleTag(taskId, tagId);
      setTask((prev) => ({
        ...prev,
        tags: updated?.tags ?? prev.tags ?? [],
        ...(updated?.version != null ? { version: updated.version } : {}),
      }));
    } catch (error) {
      errorToast(error?.message || "Failed to remove tag from task");
    }
  };

  const handleAddTagToObject = async (tagId) => {
    try {
      const updated = await api.task.toggleTag(taskId, tagId);
      setTask((prev) => ({
        ...prev,
        tags: updated?.tags ?? prev.tags ?? [],
        ...(updated?.version != null ? { version: updated.version } : {}),
      }));
    } catch (error) {
      errorToast(error?.message || "Failed to add tag to task");
    }
  };

  const closeAllDropdowns = () => {
    setShowProjectDropdown(false);
    setShowStatusDropdown(false);
    setShowPointsDropdown(false);
    setShowPriorityDropdown(false);
  };

  const handleProjectToggle = () => {
    const next = !showProjectDropdown;
    closeAllDropdowns();
    setShowProjectDropdown(next);
  };

  const handleStatusToggle = () => {
    const next = !showStatusDropdown;
    closeAllDropdowns();
    setShowStatusDropdown(next);
  };

  const handlePointsToggle = () => {
    const next = !showPointsDropdown;
    closeAllDropdowns();
    setShowPointsDropdown(next);
  };

  const handlePriorityToggle = () => {
    const next = !showPriorityDropdown;
    closeAllDropdowns();
    setShowPriorityDropdown(next);
  };

  const handleStatusChange = async (statusId) => {
    try {
      await api.task.updateTask(taskId, {
        status_id: statusId,
        version: task.version,
      });
      const updatedTask = await api.task.getTaskById(taskId);
      setTask(updatedTask);
      setShowStatusDropdown(false);
    } catch (error) {
      console.error("Error updating task status:", error);
      errorToast("Failed to update task status");
    }
  };

  const handleProjectChange = useProjectChange({
    api,
    taskId,
    task,
    setTask,
    setShowProjectDropdown,
  });

  const handlePriorityChange = usePriorityChange({
    api,
    taskId,
    task,
    setTask,
    setShowPriorityDropdown,
  });

  const handlePointsChange = usePointsChange({
    api,
    taskId,
    task,
    setTask,
    setShowPointsDropdown,
  });

  const handleAssignmentsChange = async () => {
    const updatedTask = await api.task.getTaskById(taskId);
    setTask(updatedTask);
  };

  const handleEditTask = () => {
    setIsDropdownOpen(false);
    history.push(`/task-form/${taskId}`);
  };

  const openPreview = (file) => setPreviewFile(file);
  const closePreview = () => {
    if (previewFile?.preview_url?.startsWith("blob:")) {
      URL.revokeObjectURL(previewFile.preview_url);
    }
    setPreviewFile(null);
  };

  const handleTaskFileUpload = async ({ file, preview_url }) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("task_id", taskId);
    formData.append("org_id", task?.org_id);

    try {
      const response = await api.file.createFiles(formData);
      const uploadedFile = response.results;
      if (!uploadedFile?.file_id) {
        const error = new Error("File upload failed");
        error.status = 400;
        throw error;
      }
      const newFile = { ...uploadedFile, preview_url };
      setUploadedFiles((prev) => [...prev, newFile]);
      setTask((prev) => ({ ...prev, files: [...(prev.files ?? []), newFile] }));
      successfulToast(`${file.name} uploaded successfully!`);
    } catch (err) {
      if (err?.status === 400) {
        errorToast(
          `"${file.name}" could not be uploaded. The file type may not be supported.`
        );
      } else {
        errorToast(`Failed to upload "${file.name}". Please try again.`);
      }
    }
  };

  const handleRemoveFile = async (file) => {
    try {
      await api.task.toggleFile(taskId, file.file_id);
      const updatedFiles = uploadedFiles.filter(
        (f) => f.file_id !== file.file_id
      );
      setUploadedFiles(updatedFiles);
      setTask((prev) => ({ ...prev, files: updatedFiles }));
      if (file.preview_url?.startsWith("blob:"))
        URL.revokeObjectURL(file.preview_url);
      successfulToast("File removed successfully");
    } catch (err) {
      errorToast("Failed to remove file");
    }
  };

  const onFileDownload = async (event, file) => {
    try {
      await handleFileDownload(event, file);
    } catch {
      errorToast(`Failed to download "${file.file_name}"`);
    }
  };

  if (loading) {
    return (
      <div className="task-detail-loading">
        <Loading />
      </div>
    );
  }

  if (!task) {
    return <div>Task not found</div>;
  }

  const currentStatusId =
    task.status_id ||
    task.status?.task_status_id ||
    task.status?.status_id ||
    null;
  const currentProjectId =
    task?.project_id ?? task?.project?.project_id ?? task?.project?.id ?? null;
  const currentProjectName = task?.project?.name ?? null;
  const currentStatus = currentStatusId
    ? taskStatuses.find((status) => status.task_status_id === currentStatusId)
    : null;
  const statusDisplayName =
    typeof task.status === "object" ? task.status?.name : task.status;
  const statusColor = task.status_color || currentStatus?.color;
  const currentPriorityValue = task.priority
    ? task.priority.toString().toLowerCase()
    : "low";
  const currentPointsValue =
    task.points_estimate !== undefined && task.points_estimate !== null
      ? Number(task.points_estimate)
      : 0;

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const startedAtValue = formatDateForDisplay(task.started_at);
  const dueDateValue = formatDateForDisplay(task.due_date);

  return (
    <div className="width-full max-width-full task-detail-page-container ">
      <div className="detail-header">
        <div className="header-main ">
          <h1>
            <FontAwesomeIcon icon={faListCheck} className="mr-2" />
            {task.name}
          </h1>

          <div className="action-buttons mt-4" ref={dropdownRef}>
            <FontAwesomeIcon
              icon={faEllipsisV}
              className="dropdown-toggle"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            />
            {isDropdownOpen && (
              <div className="dropdown-menu show">
                <button className="mr-2" onClick={handleEditTask}>
                  Edit Task
                </button>
                <ConfirmDelete objectType="task" id={taskId} />
              </div>
            )}
          </div>
        </div>
        <div className="header-tags">
          <FormFieldTags
            onClick={(e) => e.stopPropagation()}
            selectedTags={task.tags || []}
            handleTagRemove={handleTagRemove}
            handleAddTagToObject={handleAddTagToObject}
            objectType="tasks"
            objectId={taskId}
            objectPk="task_id"
            hideLabel={true}
          />
        </div>

        <div className="header-info">
          <div className="info-item">
            <span className="info-label">Project:</span>
            <span className="info-value">
              <ProjectDropdown
                projects={projects}
                currentProjectId={currentProjectId}
                currentProjectName={currentProjectName}
                showDropdown={showProjectDropdown}
                onToggle={handleProjectToggle}
                onClose={() => setShowProjectDropdown(false)}
                onSelect={handleProjectChange}
              />
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">Status</span>
            <span className="info-value">
              <StatusDropdown
                statuses={taskStatuses}
                currentStatusId={currentStatusId}
                statusLabel={statusDisplayName}
                statusColor={statusColor}
                showDropdown={showStatusDropdown}
                onToggle={handleStatusToggle}
                onClose={() => setShowStatusDropdown(false)}
                onSelect={handleStatusChange}
                assignee={hasAssignee}
              />
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">Priority:</span>
            <span className="info-value">
              <PriorityDropdown
                currentPriorityValue={currentPriorityValue}
                showDropdown={showPriorityDropdown}
                onToggle={handlePriorityToggle}
                onClose={() => setShowPriorityDropdown(false)}
                onSelect={handlePriorityChange}
              />
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">Assigned To</span>
            <span className="info-value">
              <TaskAssignments
                taskId={taskId}
                assignments={task?.assignments || []}
                onAssignmentsChange={handleAssignmentsChange}
                layout="vertical"
                task={task}
                projects={projects}
              />
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">Tracking Time</span>
            <span className="info-value">
              <TimeTrackingButton users={task.assignments} taskId={taskId} />
            </span>
          </div>

          <div className="info-item">
            <span className="info-label">Points</span>
            <span className="info-value">
              <PointsDropdown
                currentPointsValue={currentPointsValue}
                showDropdown={showPointsDropdown}
                onToggle={handlePointsToggle}
                onClose={() => setShowPointsDropdown(false)}
                onSelect={handlePointsChange}
              />
            </span>
          </div>

          <div className="info-item">
            <span className="info-label">Start Date:</span>
            <span className="info-value">
              <div className="date-input-wrapper date-input-wrapper--readonly">
                <FontAwesomeIcon
                  icon={faCalendarDays}
                  className="date-input-icon"
                />
                <span className="date-display-text">
                  {startedAtValue ? startedAtValue : "Select date"}
                </span>
              </div>
            </span>
          </div>

          <div className="info-item">
            <span className="info-label">Due Date:</span>
            <span className="info-value">
              <div className="date-input-wrapper date-input-wrapper--readonly">
                <FontAwesomeIcon
                  icon={faCalendarDays}
                  className="date-input-icon"
                />
                <span className="date-display-text">
                  {dueDateValue ? dueDateValue : "Select date"}
                </span>
              </div>
            </span>
          </div>
          <TaskDependencies
            task={task}
            onTaskUpdated={(updatedTask) => {
              setTask(updatedTask);
            }}
          />
        </div>
      </div>
      <div className="task-detail-split mr-4">
        <div className="task-detail-column task-detail-description info-item">
          <div className="info-label description-label">
            Description
            {isSavingDescription && (
              <span
                style={{
                  marginLeft: "8px",
                  fontSize: "0.875rem",
                  color: "#666",
                }}
              >
                Saving...
              </span>
            )}
          </div>
          <NovelEditor
            initialValue={description}
            onChange={setDescription}
            onBlur={saveDescription}
            onImageClick={({ src, alt }) =>
              setPreviewFile({
                file_type: "image/*",
                file_url: src,
                file_name: alt || "image",
              })
            }
          />
          <DragDrop
            onSuccessfulUpload={handleTaskFileUpload}
            accept="*/*"
            multiple={true}
            maxSize={20_000_000}
            uploadedFiles={uploadedFiles}
            setUploadedFiles={setUploadedFiles}
          />
          {uploadedFiles.length > 0 && (
            <div className="uploaded-files">
              <p className="file-upload-message">
                File{uploadedFiles.length > 1 ? "s" : ""} uploaded successfully:
              </p>
              <ul>
                {uploadedFiles.map((file) => (
                  <li key={file.file_id} className="file-display-wrapper">
                    <span
                      className="file-name"
                      onClick={() => openPreview(file)}
                    >
                      {file.file_name}
                    </span>
                    <a
                      href={getDownloadUrl(file)}
                      className="download-link"
                      onClick={(e) => onFileDownload(e, file)}
                      title={`Download ${file.file_name}`}
                    >
                      ↓
                    </a>
                    <button
                      className="remove-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFile(file);
                      }}
                    >
                      x
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <FilePreview file={previewFile} onClose={closePreview} />
        </div>
        <div className="task-detail-column task-detail-comments">
          <CommentsActivityLogs
            taskId={taskId}
            orgId={task.org_id}
            task={task}
          />
        </div>
      </div>
      <div className="flex-space-between mt-4">
        <h4>Subtasks</h4>
        <button
          className="btn btn-primary mr-4"
          onClick={() => history.push(`/task-form?parent_task_id=${taskId}`)}
        >
          Add Subtask
        </button>
      </div>
      <div className="mr-4 pb-16 ">
        <TaskTable
          data={subtasks}
          loading={loading}
          onDataChange={setSubtasks}
        />
      </div>
    </div>
  );
};

export default TaskDetailPage;
